/**CORE LIBRARIES IMPORT */
import { useEffect, useState } from "react";

/**DATA / SERVICES IMPORT */
import { site } from "../data/site";
import { callRpc, isBackendConfigured } from "../services/config";
import { storageUrl } from "../services/content";
import { normaliseWhatsAppNumber } from "../utils/whatsapp";

/**
 * Public site settings: static fallbacks from src/data/site.js, overridden by
 * the portal-managed `site_settings` (via the public get_public_settings RPC)
 * once the backend is configured. Never contains private data.
 * One request per page load, shared by every component.
 */
let cache = null;
let pending = null;

const MEDIA_FOCAL = { center: "center", top: "center top", bottom: "center bottom", left: "left center", right: "right center" };

/** Maps the RPC result onto the shape components use. Exported for tests. */
export function mergeSettings(remote) {
  if (!remote) return site;
  const media = {};
  for (const [slot, m] of Object.entries(remote.media || {})) {
    const src = storageUrl("site-media", m?.path);
    if (src) media[slot] = { src, alt: m.alt || "", position: MEDIA_FOCAL[m.focal] || "center" };
  }
  return {
    ...site,
    name: remote.business_name || site.name,
    seoTitle: remote.seo_title?.trim() || site.seoTitle,
    seoDescription: remote.seo_description?.trim() || site.seoDescription,
    email: remote.contact_email || site.email,
    address: remote.venue_address?.trim() || site.address, // verbatim, nothing added
    phone: remote.phone ?? site.phone,
    openingHours: remote.opening_hours ?? site.openingHours,
    openingHoursWeek: remote.opening_hours_week ?? site.openingHoursWeek,
    mapsUrl: remote.google_maps_url ?? site.mapsUrl,
    instagramUrl: remote.instagram_url ?? site.instagramUrl,
    facebookUrl: remote.facebook_url ?? site.facebookUrl,
    whatsapp: {
      number: normaliseWhatsAppNumber(remote.whatsapp_number ?? "") || site.whatsapp.number,
      defaultMessage: remote.whatsapp_message || site.whatsapp.defaultMessage,
      popupEnabled: remote.whatsapp_popup_enabled ?? site.whatsapp.popupEnabled,
      popupText: remote.whatsapp_popup_text || site.whatsapp.popupText,
    },
    media,
    loaded: true,
  };
}

function load() {
  if (!pending) {
    pending = callRpc("get_public_settings")
      .then((remote) => (cache = mergeSettings(remote)))
      .catch(() => {
        pending = null; // keep static fallbacks; retry on the next mount
        return site;
      });
  }
  return pending;
}

export default function useSiteSettings() {
  const [settings, setSettings] = useState(cache || site);

  useEffect(() => {
    if (cache || !isBackendConfigured()) return undefined;
    let cancelled = false;
    load().then((s) => !cancelled && setSettings(s));
    return () => {
      cancelled = true;
    };
  }, []);

  return settings;
}
